import { useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';

import type { TtqModelInvocationOptions } from '@kelilearning/psycheflow-contracts';

import { useAuth } from '../../context/AuthContext';
import { formatTtqModelAuditDisplay } from '../../utils/ttqModelBrand';
import { TtqWorkbenchModelMenu } from './TtqWorkbenchModelMenu';
import {
  getTtqWorkbenchModelOption,
  readStoredTtqWorkbenchModelId,
  toTtqModelInvocation,
} from './ttqWorkbenchModels';
import type { TtqDerivedProvenanceView } from './ttqibDerivedStack';
import { ensureContentAgeInPrompt, loadContentAgePromptBlock } from './ttqLearnerAgePrompt';

export type TtqRegenerateConfirmDialogProps = {
  open: boolean;
  particleId: string | null;
  particleTitle?: string | null;
  provenance: TtqDerivedProvenanceView | null;
  /** 增加节点后覆盖成员列表（确认文案与提交用） */
  memberParticleIdsOverride?: string[] | null;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: (payload: {
    model: TtqModelInvocationOptions;
    generationPrompt: string;
  }) => void;
};

function resolveBehaviorLabel(provenance: TtqDerivedProvenanceView | null): string {
  if (!provenance) return '衍生知识颗粒';
  const angle = String(provenance.ttqAngleId || provenance.pathId || '').trim();
  if (angle.includes('cttq_converge')) return '收敛品题';
  if (angle.includes('pattern_unify')) return '一解多题 · 综合同解';
  if (angle.includes('multi_solution')) return '一题多解';
  if (angle.includes('question_chain') || provenance.questionChainVariantId) {
    return provenance.variantLabel || provenance.strategyLabel || '一题多题';
  }
  return provenance.strategyLabel || provenance.variantLabel || '衍生知识颗粒';
}

function isMultiMemberProvenance(provenance: TtqDerivedProvenanceView | null): boolean {
  const angle = String(provenance?.ttqAngleId || provenance?.pathId || '').trim();
  return angle.includes('cttq_converge') || angle.includes('pattern_unify');
}

function buildRegenPromptPreview(provenance: TtqDerivedProvenanceView | null): string {
  if (!provenance) return '（无品题路径记录，将按默认策略重新生成）';
  if (provenance.generationPrompt?.trim()) {
    return provenance.generationPrompt.trim();
  }
  const lines = [
    '【任务】重新生成衍生知识颗粒（沿用上次品题参数）',
    provenance.strategyLabel || provenance.variantLabel
      ? `【策略】${provenance.strategyLabel || provenance.variantLabel}`
      : '',
    provenance.questionChainVariantId
      ? `【变式类型】${provenance.questionChainVariantId}`
      : '',
    provenance.anchorText ? `【题面锚点】\n${provenance.anchorText}` : '',
    provenance.directionPreview ? `【方向预览】\n${provenance.directionPreview}` : '',
    provenance.customPrompt ? `【补充说明】${provenance.customPrompt}` : '',
    provenance.memberParticleIds?.length
      ? `【成员颗粒】${provenance.memberParticleIds.length} 题`
      : '',
    '（未落库完整送模提示词：确认后将由后端按源题 QA + 锚点/方向重新组装，上方仅为参数摘要）',
  ].filter(Boolean);
  return lines.length > 1
    ? lines.join('\n')
    : '（上次未落库完整提示词；将沿用锚点 / 说明 / 变式参数重新发起）';
}

/** 仅当草稿含源题题干块时才作为 generationPrompt 覆盖，否则交给后端结构化组装 */
function resolveConfirmGenerationPrompt(draft: string): string {
  const text = String(draft || '').trim();
  if (!text) return '';
  if (
    /【题干】/.test(text)
    || /【源题标题】/.test(text)
    || /【源题】/.test(text)
    || /【原题】/.test(text)
    || /【成员知识颗粒】/.test(text)
    || /【代表题干】/.test(text)
    || /【成员题】/.test(text)
  ) {
    return text;
  }
  return '';
}

/** 衍生颗粒 · 重新生成前确认（可编辑提示词 + 模型选择） */
export function TtqRegenerateConfirmDialog({
  open,
  particleId,
  particleTitle,
  provenance,
  memberParticleIdsOverride = null,
  submitting = false,
  onCancel,
  onConfirm,
}: TtqRegenerateConfirmDialogProps) {
  const { user } = useAuth();
  const [modelId, setModelId] = useState(() => readStoredTtqWorkbenchModelId());
  const [promptDraft, setPromptDraft] = useState('');

  useEffect(() => {
    if (!open) return;
    setModelId(readStoredTtqWorkbenchModelId());
    const base = buildRegenPromptPreview(provenance);
    setPromptDraft(base);

    let cancelled = false;
    const userId = String(user?.id || '').trim();
    if (!userId) return undefined;

    void loadContentAgePromptBlock(userId).then((ageBlock) => {
      if (cancelled || !ageBlock) return;
      setPromptDraft((current) => ensureContentAgeInPrompt(current || base, ageBlock));
    });

    return () => {
      cancelled = true;
    };
  }, [open, particleId, provenance, user?.id]);

  const selectedModel = useMemo(
    () => toTtqModelInvocation(getTtqWorkbenchModelOption(modelId)),
    [modelId],
  );
  const previousModel = formatTtqModelAuditDisplay(provenance?.modelAudit);
  const behaviorLabel = resolveBehaviorLabel(provenance);
  const isCttq = isMultiMemberProvenance(provenance)
    && String(provenance?.ttqAngleId || provenance?.pathId || '').includes('cttq_converge');
  const isPatternUnify = String(provenance?.ttqAngleId || provenance?.pathId || '').includes('pattern_unify');
  const showMemberCount = isCttq || isPatternUnify;
  const isAddMember = Boolean(
    isCttq
    && memberParticleIdsOverride
    && memberParticleIdsOverride.length > (provenance?.memberParticleIds?.length ?? 0),
  );
  const memberCount = memberParticleIdsOverride?.length
    ?? provenance?.memberParticleIds?.length
    ?? 0;
  const canSubmit = Boolean(promptDraft.trim()) && !submitting;

  if (!open || !particleId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={() => {
        if (!submitting) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-xl border border-border bg-surface p-4 shadow-xl"
        role="dialog"
        aria-modal
        aria-labelledby="ttq-regen-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <RefreshCw size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <p id="ttq-regen-title" className="text-sm font-semibold text-ink">
              {isAddMember
                ? '确认加入节点并更新聚合？'
                : isCttq
                  ? '确认重新聚合？'
                  : '确认重新生成？'}
            </p>
            <p className="mt-1 text-xs text-muted">
              {isAddMember
                ? '将用新成员集合重新聚合；可先改提示词。成功后移出当前代表题。'
                : isCttq
                  ? '可编辑下方提示词后再次聚合；成功后会移出当前这颗代表题。'
                  : '可编辑下方提示词后发起；成功后会移出当前这颗衍生颗粒。'}
            </p>
          </div>
        </div>

        <div className="mt-3 space-y-1.5 rounded-lg border border-border bg-canvas/50 px-3 py-2 text-xs text-ink">
          <p>
            <span className="text-muted">类型：</span>
            <span className="font-medium">{behaviorLabel}</span>
          </p>
          {particleTitle ? (
            <p className="line-clamp-2">
              <span className="text-muted">当前颗粒：</span>
              <span className="font-medium">{particleTitle}</span>
            </p>
          ) : null}
          {showMemberCount && memberCount > 0 ? (
            <p>
              <span className="text-muted">成员题：</span>
              <span className="font-medium">
                {memberCount} 题
                {isAddMember ? '（含新加入）' : ''}
              </span>
            </p>
          ) : null}
          {previousModel ? (
            <p className="flex items-center gap-1.5">
              <span className="text-muted">上次模型：</span>
              {previousModel.iconUrl ? (
                <img src={previousModel.iconUrl} alt="" className="h-3.5 w-3.5 rounded-sm" />
              ) : null}
              <span className="font-medium">{previousModel.label}</span>
            </p>
          ) : null}
        </div>

        <div className="mt-3">
          <p className="mb-1.5 text-[11px] font-medium text-muted">发起提示词（可编辑）</p>
          <textarea
            value={promptDraft}
            onChange={(event) => setPromptDraft(event.target.value)}
            disabled={submitting}
            rows={10}
            spellCheck={false}
            className="max-h-48 w-full resize-y overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-canvas/60 px-3 py-2 font-sans text-[11px] leading-relaxed text-ink outline-none focus:border-primary/40 disabled:opacity-60"
          />
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <p className="text-[11px] font-medium text-muted">本次模型</p>
          <TtqWorkbenchModelMenu
            value={modelId}
            disabled={submitting}
            onChange={(option) => setModelId(option.id)}
          />
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="flex-1 rounded-lg border border-border py-2 text-sm text-muted hover:bg-black/[0.03] disabled:opacity-50"
            disabled={submitting}
            onClick={onCancel}
          >
            取消
          </button>
          <button
            type="button"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            disabled={!canSubmit}
            onClick={() => onConfirm({
              model: selectedModel,
              generationPrompt: resolveConfirmGenerationPrompt(promptDraft),
            })}
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {submitting
              ? (isCttq ? '聚合中…' : '生成中…')
              : isAddMember
                ? '确认更新聚合'
                : (isCttq ? '确认重新聚合' : '确认重新生成')}
          </button>
        </div>
      </div>
    </div>
  );
}
